import React, { useState, useEffect, useMemo } from 'react';
import {
   Box,
   VStack,
   HStack,
   FormControl,
   FormLabel,
   Input,
   Select,
   Button,
   Text,
   SimpleGrid,
   NumberInput,
   NumberInputField,
   NumberInputStepper,
   NumberIncrementStepper,
   NumberDecrementStepper,
   Card,
   CardHeader,
   CardBody,
   Heading,
   Switch,
   Badge,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalFooter,
   ModalBody,
   ModalCloseButton,
   Checkbox,
   Collapse,
   IconButton,
   Flex,
   Divider,
   useColorModeValue,
   useToast,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, DragHandleIcon } from '@chakra-ui/icons';
import { FaCopy } from 'react-icons/fa';
import { getCompanyById, updateDynamicBarcode, getCompanies } from '../../../../redux/actions/company';
import { useDispatch, useSelector } from 'react-redux';
import { useUser, useGlobal } from 'contexts';
import { mergeBarcodeSetup } from '../../../../utils/util';

// Updated default barcode setup configuration with digitToChar for DAY, MONTH, and YEAR
const defaultBarcodeSetup = {
   isEnable: false,
   content: [
      {
         key: 'DAY',
         ordinal: 3,
         digitToChar: false,
         charToSpecial: false,
         isReverse: false,
         start: 0,
         end: 2,
         length: 2,
         countFromLast: false,
      },
      {
         key: 'MONTH',
         ordinal: 4,
         digitToChar: false,
         charToSpecial: false,
         isReverse: false,
         start: 0,
         end: 2,
         length: 2,
         countFromLast: false,
      },
      {
         key: 'YEAR',
         ordinal: 5,
         digitToChar: false,
         charToSpecial: false,
         isReverse: false,
         start: 0,
         end: 4,
         length: 4,
         countFromLast: false,
      },
      {
         key: 'RATE',
         ordinal: 2,
         digitToChar: false,
         charToSpecial: false,
         operator: 'NONE',
         operatorValue: 0,
         isReverse: false,
         start: 0,
         end: 10,
         length: 10,
         countFromLast: false,
      },
      {
         key: 'IMEI',
         ordinal: 1,
         charToSpecial: false,
         isReverse: false,
         start: 0,
         end: 6,
         length: 6,
         countFromLast: false,
      },
      {
         key: 'WAREHOUSE',
         ordinal: 6,
         charToSpecial: false,
         isReverse: false,
         start: 0,
         end: 3,
         length: 3,
         countFromLast: false,
      },
      {
         key: 'ITEMCODE',
         ordinal: 7,
         charToSpecial: false,
         isReverse: false,
         start: 0,
         end: 8,
         length: 8,
         countFromLast: false,
      },
      {
         key: 'VENDORCODE',
         ordinal: 8,
         charToSpecial: false,
         isReverse: false,
         start: 0,
         end: 4,
         length: 4,
         countFromLast: false,
      },
   ],
   alterations: [{ original: '0', replace: 'A' }],
   modifications: [{ original: 'A', replace: '@' }],
};

export default function DynamicBarcodeForm({ isdynamicBarcode = true, onClose = () => { }, initialConfig = null }) {
   const dispatch = useDispatch();
   const companyId = localStorage.getItem('companyId');
   const [barcodeSetup, setBarcodeSetup] = useState({});
   const [formData, setFormData] = useState({});
   const [generatedBarcode, setGeneratedBarcode] = useState('');
   const [expandedFields, setExpandedFields] = useState({});
   const [draggedItem, setDraggedItem] = useState(null);
   const [isLoading, setIsLoading] = useState(true);
   const { company } = useGlobal();
   // Frontend-only field toggles (not sent to backend)
   const [fieldToggles, setFieldToggles] = useState({});
   const toast = useToast();
   const { user } = useUser();

   // Get company data from Redux store
   const companyData = useSelector((state) => state?.company?.getCompanyById?.data);

   // Handle main isEnable toggle
   const handleIsEnableToggle = (isEnabled) => {
      setBarcodeSetup((prev) => ({
         ...prev,
         isEnable: isEnabled,
      }));
   };
   const colors = {
      // Modal colors
      modalOverlay: useColorModeValue('blackAlpha.300', 'blackAlpha.600'),
      modalBg: useColorModeValue('white', 'gray.800'),
      modalText: useColorModeValue('gray.800', 'white'),
      modalBody: useColorModeValue('gray.50', 'gray.900'),

      // Header colors
      headingColor: useColorModeValue('blue.600', 'blue.300'),
      labelColor: useColorModeValue('gray.700', 'gray.300'),
      closeButtonColor: useColorModeValue('gray.600', 'gray.400'),

      // Card colors
      cardBg: useColorModeValue('white', 'gray.800'),
      cardBorder: useColorModeValue('gray.200', 'gray.600'),

      // Text colors
      primaryText: useColorModeValue('gray.800', 'white'),
      secondaryText: useColorModeValue('gray.600', 'gray.400'),
      labelText: useColorModeValue('gray.700', 'gray.300'),

      // Input colors
      inputBg: useColorModeValue('white', 'gray.600'),
      inputBorder: useColorModeValue('gray.300', 'gray.500'),
      inputText: useColorModeValue('gray.800', 'white'),
      inputPlaceholder: useColorModeValue('gray.400', 'gray.500'),

      // Border colors
      dividerBorder: useColorModeValue('gray.200', 'gray.600'),

      // Drag and drop colors
      dragBorderActive: useColorModeValue('blue.300', 'blue.500'),
      dragBorderInactive: useColorModeValue('gray.200', 'gray.600'),
      dragBorderDisabled: useColorModeValue('red.200', 'red.500'),
      dragBgActive: useColorModeValue('blue.50', 'blue.900'),
      dragBgInactive: useColorModeValue('white', 'gray.700'),
      dragBgDisabled: useColorModeValue('red.50', 'red.900'),
      dragHoverBorder: useColorModeValue('blue.300', 'blue.500'),
      dragIconColor: useColorModeValue('gray.400', 'gray.500'),

      // Button colors
      buttonOutlineBorder: useColorModeValue('gray.300', 'gray.500'),
      buttonOutlineText: useColorModeValue('gray.700', 'gray.300'),
      buttonGhostHover: useColorModeValue('gray.100', 'gray.700'),

      // Disabled overlay colors
      disabledOverlayBg: useColorModeValue('white', 'gray.700'),
      disabledOverlayBorder: useColorModeValue('orange.300', 'orange.500'),
      disabledOverlayTitle: useColorModeValue('orange.600', 'orange.300'),
      disabledOverlayText: useColorModeValue('gray.600', 'gray.400'),

      // Barcode display colors
      barcodeBg: useColorModeValue('gray.50', 'gray.700'),
      barcodeBorder: useColorModeValue('blue.200', 'blue.400'),
      barcodeText: useColorModeValue('blue.700', 'blue.300'),

      // Stepper colors
      stepperColor: useColorModeValue('gray.600', 'gray.400'),

      // Select colors
      selectBg: useColorModeValue('white', 'gray.600'),
      selectBorder: useColorModeValue('gray.300', 'gray.500'),
      selectText: useColorModeValue('gray.800', 'white'),
   };


   const margeBarcodeSetup = useMemo(() => {
      if (company?.barCodeSetup) {
         return mergeBarcodeSetup(defaultBarcodeSetup, company?.barCodeSetup);
      }
      return mergeBarcodeSetup(defaultBarcodeSetup, barcodeSetup);
   }, [barcodeSetup, company?.barCodeSetup])

   useEffect(() => {
      if (company?.id) {


         const selectedKeys = new Set(company?.barCodeSetup?.content?.map(item => item.key));

         const result = defaultBarcodeSetup?.content?.reduce((acc, item) => {
            acc[item.key] = selectedKeys.has(item.key);
            return acc;
         }, {});
         setFieldToggles(result)

         setBarcodeSetup(margeBarcodeSetup)
         setIsLoading(false)
      }
      return () => { }
   }, [company?.id])


   // Fetch company data when component mounts
   useEffect(() => {
      if (user?.loginhistory?.companyId) {
         dispatch(getCompanies());
      }
   }, [dispatch, user]);

   // Custom close handler to refresh data
   const handleClose = () => {
      // Refresh data before closing to get latest saved configuration
      if (user?.loginhistory?.companyId) {
         dispatch(getCompanyById(user.loginhistory.companyId));
      }
      onClose();
   };

   // Initialize form data based on content keys - FIXED INITIAL TOGGLES
   useEffect(() => {
      // Only initialize if not loading and barcodeSetup is properly loaded
      if (!isLoading && barcodeSetup?.content?.length > 0 && Object.keys(fieldToggles)?.length === 0) {

         const initialFormData = {};
         const expandedState = {};
         const initialToggles = {};

         barcodeSetup?.content?.forEach((item) => {
            initialFormData[item.key] = '';
            expandedState[item.key] = false;

            // FIXED: Set YEAR, WAREHOUSE, ITEMCODE, and VENDORCODE to disabled by default
            if (['YEAR', 'WAREHOUSE', 'ITEMCODE', 'VENDORCODE'].includes(item.key)) {
               initialToggles[item.key] = false;
            } else {
               initialToggles[item.key] = true;
            }
         });

         setFormData(initialFormData);
         setExpandedFields(expandedState);
         setFieldToggles(initialToggles);
      }
   }, [barcodeSetup?.content?.length, isLoading]); // Remove formData and fieldToggles from dependencies

   const handleInputChange = (field, value) => {
      setFormData((prev) => ({
         ...prev,
         [field]: value,
      }));
   };

   const handleContentChange = (index, field, value) => {
      setBarcodeSetup((prev) => ({
         ...prev,
         content: prev.content.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
      }));
   };

   const handleAlterationChange = (index, field, value) => {
      setBarcodeSetup((prev) => ({
         ...prev,
         alterations: prev.alterations.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
      }));
   };

   const handleModificationChange = (index, field, value) => {
      setBarcodeSetup((prev) => ({
         ...prev,
         modifications: prev.modifications.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
      }));
   };

   // Handle field enable/disable toggle (frontend only)
   const handleFieldToggle = (fieldKey, isEnabled) => {
      setFieldToggles((prev) => ({
         ...prev,
         [fieldKey]: isEnabled,
      }));
   };

   // Check if all alterations are properly filled
   const canAddAlteration = () => {
      return barcodeSetup.alterations.every((alt) => alt.original.trim() !== '' && alt.replace.trim() !== '');
   };

   // Check if all modifications are properly filled
   const canAddModification = () => {
      return barcodeSetup.modifications.every((mod) => mod.original.trim() !== '' && mod.replace.trim() !== '');
   };

   const addNewAlteration = () => {
      if (canAddAlteration()) {
         const newAlteration = { original: '', replace: '' };
         setBarcodeSetup((prev) => ({
            ...prev,
            alterations: [...prev.alterations, newAlteration],
         }));
      } else {
         console.log('error');
      }
   };

   const removeAlteration = (index) => {
      setBarcodeSetup((prev) => ({
         ...prev,
         alterations: prev.alterations.filter((_, i) => i !== index),
      }));
   };

   const addNewModification = () => {
      if (canAddModification()) {
         const newModification = { original: '', replace: '' };
         setBarcodeSetup((prev) => ({
            ...prev,
            modifications: [...prev.modifications, newModification],
         }));
      } else {
         console.log('error');
      }
   };

   const removeModification = (index) => {
      setBarcodeSetup((prev) => ({
         ...prev,
         modifications: prev.modifications.filter((_, i) => i !== index),
      }));
   };

   const toggleFieldExpanded = (fieldKey) => {
      setExpandedFields((prev) => ({
         ...prev,
         [fieldKey]: !prev[fieldKey],
      }));
   };

   // Drag and Drop functions
   const handleDragStart = (e, index) => {
      setDraggedItem(index);
      e.dataTransfer.effectAllowed = 'move';
   };

   const handleDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
   };

   const handleDrop = (e, dropIndex) => {
      e.preventDefault();
      if (draggedItem === null || draggedItem === dropIndex) return;

      setBarcodeSetup((prev) => {
         const newContent = [...prev.content];
         const draggedContent = newContent[draggedItem];

         // Remove the dragged item
         newContent.splice(draggedItem, 1);

         // Insert at new position
         const insertIndex = draggedItem < dropIndex ? dropIndex - 1 : dropIndex;
         newContent.splice(insertIndex, 0, draggedContent);

         // Update ordinals to maintain sequence
         newContent.forEach((item, index) => {
            item.ordinal = index + 1;
         });

         return {
            ...prev,
            content: newContent,
         };
      });

      setDraggedItem(null);
   };

   // Updated utility function to handle padding/truncation
   const padOrTruncate = (str, length) => {
      if (!str) return ''; // Return empty string for null/undefined/empty values

      // Convert to string if it's a number
      const stringValue = String(str);

      // If string is longer than length, truncate it
      if (stringValue?.length > length) {
         return stringValue.substring(0, length);
      }

      // Return the string as is without padding
      return stringValue;
   };

   // FIXED: Custom digit to character conversion using ONLY defined alterations
   const convertDigitToChar = (digit) => {
      // Create a mapping object from alterations
      const digitMap = {};
      barcodeSetup?.alterations?.forEach(({ original, replace }) => {
         if (original && replace) {
            digitMap[original] = replace;
         }
      });

      // Return the mapped character ONLY if it exists in our mapping, otherwise return original digit
      return digitMap[digit] !== undefined ? digitMap[digit] : digit;
   };

   const processRate = (rate, contentItem) => {
      let processedRate = parseFloat(rate) || 0;

      if (contentItem.operator !== 'NONE') {
         switch (contentItem.operator) {
            case 'ADD':
               processedRate += contentItem.operatorValue;
               break;
            case 'SUBTRACT':
               processedRate -= contentItem.operatorValue;
               break;
            case 'MULTIPLY':
               processedRate *= contentItem.operatorValue;
               break;
            case 'DIVIDE':
               processedRate =
                  contentItem.operatorValue !== 0 ? processedRate / contentItem.operatorValue : processedRate;
               break;
            default:
               break;
         }
      }

      if (contentItem.digitToChar) {
         // Convert each digit using ONLY the custom mapping
         const rateStr = Math.round(processedRate).toString();
         return rateStr
            .split('')
            .map((digit) => convertDigitToChar(digit))
            .join('');
      }

      return Math.round(processedRate).toString();
   };

   // FIXED: Character replacements should only use exact mappings
   // FIXED: Character replacements should only use exact mappings for each character in the string
   const applyCharacterReplacements = (str, contentItem) => {
      let result = str;

      // Apply alterations only if digitToChar is enabled for this field
      if (contentItem.digitToChar) {
         // Create a mapping object from alterations for faster lookup
         const alterationMap = {};
         barcodeSetup.alterations.forEach(({ original, replace }) => {
            if (original && replace) {
               alterationMap[original] = replace;
            }
         });

         // Replace each character individually if it exists in our mapping
         result = result
            .split('')
            .map((char) => {
               return alterationMap[char] !== undefined ? alterationMap[char] : char;
            })
            .join('');
      }

      // Apply modifications only if charToSpecial is enabled for this field
      if (contentItem.charToSpecial) {
         // Create a mapping object from modifications for faster lookup
         const modificationMap = {};
         barcodeSetup.modifications.forEach(({ original, replace }) => {
            if (original && replace) {
               modificationMap[original] = replace;
            }
         });

         // Replace each character individually if it exists in our mapping
         result = result
            .split('')
            .map((char) => {
               return modificationMap[char] !== undefined ? modificationMap[char] : char;
            })
            .join('');
      }

      return result;
   };

   const reverseString = (str) => {
      return str.split('').reverse().join('');
   };

   const extractSubstring = (str, contentItem) => {
      if (!str) return '';

      let { start, end, countFromLast } = contentItem;

      if (countFromLast) {
         // When counting from last, start and end positions are relative to the end
         const strLen = str?.length;

         // Calculate actual start position from the end
         const actualStart = Math.max(0, strLen - end);

         // Calculate actual end position from the end
         const actualEnd = Math.max(actualStart, strLen - start);

         // Extract the substring
         return str.substring(actualStart, actualEnd);
      } else {
         // Regular extraction from the beginning
         return str.substring(start, end);
      }
   };

   const generateBarcode = () => {
      try {
         let barcode = '';
         // Only use enabled fields for barcode generation
         const enabledContent = barcodeSetup.content
            .filter((item) => fieldToggles[item.key])
            .sort((a, b) => a.ordinal - b.ordinal);

         enabledContent.forEach((contentItem) => {
            const fieldValue = formData[contentItem.key] || '';

            // Skip empty fields entirely - don't add anything to barcode
            if (!fieldValue || fieldValue === '') {
               return; // Skip this iteration
            }

            let processedValue = '';

            if (contentItem.key === 'RATE') {
               const rateValue = parseFloat(fieldValue);
               // Skip if rate is 0 or NaN
               if (isNaN(rateValue) || rateValue === 0) {
                  return;
               }
               processedValue = processRate(fieldValue, contentItem);
            } else if (contentItem.key === 'DAY') {
               const dayValue = parseInt(fieldValue) || 0;
               // Skip if day is 0 or invalid
               if (dayValue < 1 || dayValue > 31) {
                  return;
               }
               processedValue = String(dayValue);
               if (contentItem.digitToChar) {
                  processedValue = processedValue
                     .split('')
                     .map((digit) => convertDigitToChar(digit))
                     .join('');
               }
            } else if (contentItem.key === 'MONTH') {
               const monthValue = parseInt(fieldValue) || 0;
               // Skip if month is 0 or invalid
               if (monthValue < 1 || monthValue > 12) {
                  return;
               }
               processedValue = String(monthValue);
               if (contentItem.digitToChar) {
                  processedValue = processedValue
                     .split('')
                     .map((digit) => convertDigitToChar(digit))
                     .join('');
               }
            } else if (contentItem.key === 'YEAR') {
               const yearValue = parseInt(fieldValue) || 0;
               // Skip if year is 0 or invalid
               if (yearValue <= 0) {
                  return;
               }
               if (yearValue < 100) {
                  processedValue = `20${String(yearValue)}`;
               } else {
                  processedValue = String(yearValue);
               }
               if (contentItem.digitToChar) {
                  processedValue = processedValue
                     .split('')
                     .map((digit) => convertDigitToChar(digit))
                     .join('');
               }
            } else {
               // For other fields, skip if empty
               if (!fieldValue.trim()) {
                  return;
               }
               processedValue = fieldValue;
            }

            processedValue = extractSubstring(processedValue, contentItem);
            processedValue = padOrTruncate(processedValue, contentItem?.length);

            // Skip if processed value is empty after all operations
            if (!processedValue) {
               return;
            }

            if (contentItem.isReverse) {
               processedValue = reverseString(processedValue);
            }

            // Apply character replacements based on field-specific settings
            processedValue = applyCharacterReplacements(processedValue, contentItem);

            barcode += processedValue;
         });

         setGeneratedBarcode(barcode);
      } catch (error) {
         console.error('Barcode Generation Error:', error);
      }
   };

   const handleBarcodeSave = async () => {
      try {
         // Filter out disabled fields from content
         const enabledContent = barcodeSetup.content.filter((item) => fieldToggles[item.key]);

         // Prepare data that would be sent to backend (only enabled fields)
         const backendData = {
            id: companyId,
            barCodeSetup: {
               ...barcodeSetup,
               content: enabledContent, // Only enabled fields
            },
         };

         // Call the update action
         await dispatch(updateDynamicBarcode(backendData));
         dispatch(getCompanies());
      } catch (error) {
         console.error('Save error:', error);
      }
   };

   const resetForm = async () => {
      try {
         // Reset local state to empty structure
         setFormData({});
         setGeneratedBarcode('');
         setFieldToggles({});

         // Reset barcode setup to empty structure
         const emptyBarcodeSetup = {
            isEnable: false,
            content: [],
            alterations: [],
            modifications: [],
         };

         setBarcodeSetup(emptyBarcodeSetup);

         // Clear localStorage
         localStorage.removeItem('barcodeFormData');
         localStorage.removeItem('barcodeFormValues');
         localStorage.removeItem('barcodeFieldToggles');

         // Send empty configuration to API
         const resetData = {
            id: companyId,
            barCodeSetup: {
               isEnable: false,
               content: [],
               alterations: [],
               modifications: [],
            },
         };

         // Call the API to reset/clear the barcode configuration
         await dispatch(updateDynamicBarcode(resetData));
      } catch (error) {
         console.error('Reset error:', error);
      }
   };

   const copyToClipboard = () => {
      navigator.clipboard
         .writeText(generatedBarcode)
         .then(() => {
            toast({
               title: 'Copied!',
               description: 'Barcode copied to clipboard successfully.',
               status: 'success',
               duration: 2000,
               isClosable: true,
            });
         })
         .catch(() => {
            toast({
               title: 'Copy Failed',
               description: 'Failed to copy barcode to clipboard.',
               status: 'error',
               duration: 2000,
               isClosable: true,
            });
         });
   };

   const getInputType = (fieldKey) => {
      switch (fieldKey) {
         case 'RATE':
         case 'DAY':
         case 'MONTH':
         case 'YEAR':
            return 'number';
         default:
            return 'text';
      }
   };

   const getPlaceholder = (fieldKey) => {
      switch (fieldKey) {
         case 'IMEI':
            return 'Enter 15-digit IMEI';
         case 'RATE':
            return 'Enter rate value';
         case 'DAY':
            return 'Enter day (1-31)';
         case 'MONTH':
            return 'Enter month (1-12)';
         case 'YEAR':
            return 'Enter year (e.g., 2024)';
         case 'WAREHOUSE':
            return 'Enter warehouse code';
         case 'ITEMCODE':
            return 'Enter item code';
         case 'VENDORCODE':
            return 'Enter vendor code';
         default:
            return `Enter ${fieldKey}`;
      }
   };

   // Show loading state while data is being fetched
   if (isLoading) {
      return (
         <Modal isOpen={isdynamicBarcode} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
               <ModalBody>
                  <VStack spacing={4} py={8}>
                     <Text>Loading barcode configuration...</Text>
                  </VStack>
               </ModalBody>
            </ModalContent>
         </Modal>
      );
   }

   return (
      <Modal isOpen={isdynamicBarcode} onClose={onClose} scrollBehavior="inside" closeOnOverlayClick={false} size="6xl">
         <ModalOverlay />
         <ModalContent maxH="90vh" bg={colors.modalBg} color={colors.modalText}>
            <ModalHeader bg={colors.modalBg}>
               <HStack justify="space-between" align="center" w="100%">
                  <VStack align="start" spacing={1}>
                     <Heading color={colors.headingColor} size="lg">
                        Barcode Generator Setup
                     </Heading>
                     {companyData?.barCodeSetup && (
                        <Badge colorScheme="green" fontSize="xs">
                           Configuration loaded from server
                        </Badge>
                     )}
                  </VStack>
                  <HStack spacing={3} align="center">
                     <FormLabel
                        htmlFor="main-toggle"
                        mb={0}
                        fontSize="sm"
                        fontWeight="medium"
                        color={colors.labelColor}>
                        Enable Generator
                     </FormLabel>
                     <Switch
                        id="main-toggle"
                        isChecked={barcodeSetup.isEnable}
                        onChange={(e) => handleIsEnableToggle(e.target.checked)}
                        colorScheme="green"
                        size="lg"
                     />
                     <Badge
                        colorScheme={barcodeSetup.isEnable ? 'green' : 'red'}
                        fontSize="sm"
                        px={3}
                        py={1}
                        borderRadius="md">
                        {barcodeSetup.isEnable ? 'Enabled' : 'Disabled'}
                     </Badge>
                  </HStack>
               </HStack>
            </ModalHeader>
            <ModalCloseButton onClick={handleClose} color={colors.closeButtonColor} />

            <ModalBody bg={colors.modalBody}>
               <Box
                  filter={!barcodeSetup.isEnable ? 'blur(3px)' : 'none'}
                  opacity={!barcodeSetup.isEnable ? 0.5 : 1}
                  pointerEvents={!barcodeSetup.isEnable ? 'none' : 'auto'}
                  transition="all 0.3s ease-in-out">
                  {!barcodeSetup.isEnable && (
                     <Box
                        position="absolute"
                        top="50%"
                        left="50%"
                        transform="translate(-50%, -50%)"
                        zIndex={10}
                        bg={colors.disabledOverlayBg}
                        p={6}
                        borderRadius="lg"
                        boxShadow="xl"
                        border="2px solid"
                        borderColor={colors.disabledOverlayBorder}
                        textAlign="center">
                        <Text fontSize="lg" fontWeight="bold" color={colors.disabledOverlayTitle} mb={2}>
                           Barcode Generator Disabled
                        </Text>
                        <Text color={colors.disabledOverlayText}>
                           Please enable the generator using the toggle switch above to access all features.
                        </Text>
                     </Box>
                  )}

                  <VStack spacing={4} align="stretch">
                     {/* Main Content Split into Two Columns */}
                     <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                        {/* Left Column - Barcode Data Fields */}
                        <Card bg={colors.cardBg} borderColor={colors.cardBorder}>
                           <CardHeader>
                              <Heading size="md" color={colors.primaryText}>
                                 Barcode Data Fields
                              </Heading>
                              <Text fontSize="sm" color={colors.secondaryText}>
                                 Toggle fields on/off and drag to reorder
                              </Text>
                           </CardHeader>
                           <CardBody maxH="60vh" overflowY="auto">
                              <VStack spacing={4}>
                                 {barcodeSetup.content.map((contentItem, index) => {
                                    const getBorderColor = () => {
                                       if (draggedItem === index) return colors.dragBorderActive;
                                       if (!fieldToggles[contentItem.key]) return colors.dragBorderDisabled;
                                       return colors.dragBorderInactive;
                                    };

                                    const getBgColor = () => {
                                       if (draggedItem === index) return colors.dragBgActive;
                                       if (!fieldToggles[contentItem.key]) return colors.dragBgDisabled;
                                       return colors.dragBgInactive;
                                    };

                                    return (
                                       <Box
                                          key={`${contentItem.key}-${index}`}
                                          w="100%"
                                          p={4}
                                          borderWidth="2px"
                                          borderRadius="lg"
                                          borderColor={getBorderColor()}
                                          bg={getBgColor()}
                                          opacity={!fieldToggles[contentItem.key] ? 0.6 : 1}
                                          cursor="move"
                                          draggable
                                          onDragStart={(e) => handleDragStart(e, index)}
                                          onDragOver={handleDragOver}
                                          onDrop={(e) => handleDrop(e, index)}
                                          transition="all 0.2s"
                                          _hover={{
                                             borderColor: colors.dragHoverBorder,
                                             shadow: 'md',
                                          }}>
                                          <HStack justify="space-between" mb={3}>
                                             <HStack>
                                                <DragHandleIcon color={colors.dragIconColor} />
                                                <Text fontWeight="bold" fontSize="md" color={colors.primaryText}>
                                                   {contentItem.key}
                                                </Text>
                                                <Badge
                                                   colorScheme={fieldToggles[contentItem.key] ? 'green' : 'red'}
                                                   fontSize="xs">
                                                   {fieldToggles[contentItem.key] ? 'Enabled' : 'Disabled'}
                                                </Badge>
                                             </HStack>
                                             <HStack>
                                                <Switch
                                                   isChecked={fieldToggles[contentItem.key] || false}
                                                   onChange={(e) =>
                                                      handleFieldToggle(contentItem.key, e.target.checked)
                                                   }
                                                   colorScheme="green"
                                                   size="sm"
                                                />
                                                <IconButton
                                                   size="md"
                                                   icon={
                                                      expandedFields[contentItem.key] ? (
                                                         <ChevronUpIcon />
                                                      ) : (
                                                         <ChevronDownIcon />
                                                      )
                                                   }
                                                   onClick={() => toggleFieldExpanded(contentItem.key)}
                                                   variant="ghost"
                                                   color={colors.closeButtonColor}
                                                />
                                             </HStack>
                                          </HStack>

                                          <FormControl mb={3}>
                                             <FormLabel fontSize="sm" color={colors.labelText}>
                                                Value
                                             </FormLabel>
                                             <Input
                                                type={getInputType(contentItem.key)}
                                                value={formData[contentItem.key] || ''}
                                                onChange={(e) => handleInputChange(contentItem.key, e.target.value)}
                                                placeholder={getPlaceholder(contentItem.key)}
                                                size="md"
                                                isDisabled={!fieldToggles[contentItem.key]}
                                                bg={colors.inputBg}
                                                borderColor={colors.inputBorder}
                                                color={colors.inputText}
                                                _placeholder={{ color: colors.inputPlaceholder }}
                                             />
                                          </FormControl>

                                          <Collapse in={expandedFields[contentItem.key]}>
                                             <VStack spacing={3} align="stretch">
                                                <Divider borderColor={colors.dividerBorder} />

                                                {/* Start, End, Length */}
                                                <SimpleGrid columns={3} spacing={2}>
                                                   <FormControl>
                                                      <FormLabel fontSize="xs" color={colors.labelText}>
                                                         Start
                                                      </FormLabel>
                                                      <NumberInput
                                                         size="sm"
                                                         value={contentItem.start}
                                                         onChange={(value) =>
                                                            handleContentChange(index, 'start', parseInt(value) || 0)
                                                         }
                                                         min={0}
                                                         isDisabled={!fieldToggles[contentItem.key]}>
                                                         <NumberInputField
                                                            bg={colors.inputBg}
                                                            borderColor={colors.inputBorder}
                                                            color={colors.inputText}
                                                         />
                                                         <NumberInputStepper>
                                                            <NumberIncrementStepper color={colors.stepperColor} />
                                                            <NumberDecrementStepper color={colors.stepperColor} />
                                                         </NumberInputStepper>
                                                      </NumberInput>
                                                   </FormControl>
                                                   <FormControl>
                                                      <FormLabel fontSize="xs" color={colors.labelText}>
                                                         End
                                                      </FormLabel>
                                                      <NumberInput
                                                         size="sm"
                                                         value={contentItem.end}
                                                         onChange={(value) =>
                                                            handleContentChange(index, 'end', parseInt(value) || 1)
                                                         }
                                                         min={1}
                                                         isDisabled={!fieldToggles[contentItem.key]}>
                                                         <NumberInputField
                                                            bg={colors.inputBg}
                                                            borderColor={colors.inputBorder}
                                                            color={colors.inputText}
                                                         />
                                                         <NumberInputStepper>
                                                            <NumberIncrementStepper color={colors.stepperColor} />
                                                            <NumberDecrementStepper color={colors.stepperColor} />
                                                         </NumberInputStepper>
                                                      </NumberInput>
                                                   </FormControl>
                                                   <FormControl>
                                                      <FormLabel fontSize="xs" color={colors.labelText}>
                                                         Length
                                                      </FormLabel>
                                                      <NumberInput
                                                         size="sm"
                                                         value={contentItem?.length}
                                                         onChange={(value) =>
                                                            handleContentChange(index, 'length', parseInt(value) || 1)
                                                         }
                                                         min={1}
                                                         max={20}
                                                         isDisabled={!fieldToggles[contentItem.key]}>
                                                         <NumberInputField
                                                            bg={colors.inputBg}
                                                            borderColor={colors.inputBorder}
                                                            color={colors.inputText}
                                                         />
                                                         <NumberInputStepper>
                                                            <NumberIncrementStepper color={colors.stepperColor} />
                                                            <NumberDecrementStepper color={colors.stepperColor} />
                                                         </NumberInputStepper>
                                                      </NumberInput>
                                                   </FormControl>
                                                </SimpleGrid>

                                                {/* Checkboxes */}
                                                <SimpleGrid columns={2} spacing={2}>
                                                   <Checkbox
                                                      isChecked={contentItem.isReverse}
                                                      onChange={(e) =>
                                                         handleContentChange(index, 'isReverse', e.target.checked)
                                                      }
                                                      size="sm"
                                                      isDisabled={!fieldToggles[contentItem.key]}
                                                      colorScheme="green">
                                                      <Text color={colors.labelText}>Reverse</Text>
                                                   </Checkbox>
                                                   <Checkbox
                                                      isChecked={contentItem.charToSpecial}
                                                      onChange={(e) =>
                                                         handleContentChange(index, 'charToSpecial', e.target.checked)
                                                      }
                                                      size="sm"
                                                      isDisabled={!fieldToggles[contentItem.key]}
                                                      colorScheme="green">
                                                      <Text color={colors.labelText}>Char to Special</Text>
                                                   </Checkbox>
                                                   <Checkbox
                                                      isChecked={contentItem.countFromLast}
                                                      onChange={(e) =>
                                                         handleContentChange(index, 'countFromLast', e.target.checked)
                                                      }
                                                      size="sm"
                                                      isDisabled={!fieldToggles[contentItem.key]}
                                                      colorScheme="green">
                                                      <Text color={colors.labelText}>Count From Last</Text>
                                                   </Checkbox>

                                                   {/* Add digitToChar checkbox for DAY, MONTH, and YEAR fields */}
                                                   {(contentItem.key === 'DAY' ||
                                                      contentItem.key === 'MONTH' ||
                                                      contentItem.key === 'YEAR') && (
                                                         <Checkbox
                                                            isChecked={contentItem.digitToChar}
                                                            onChange={(e) =>
                                                               handleContentChange(index, 'digitToChar', e.target.checked)
                                                            }
                                                            size="sm"
                                                            isDisabled={!fieldToggles[contentItem.key]}
                                                            colorScheme="green">
                                                            <Text color={colors.labelText}>Digit to Char</Text>
                                                         </Checkbox>
                                                      )}
                                                </SimpleGrid>

                                                {/* RATE specific fields */}
                                                {contentItem.key === 'RATE' && (
                                                   <VStack spacing={2} align="stretch">
                                                      <Checkbox
                                                         isChecked={contentItem.digitToChar}
                                                         onChange={(e) =>
                                                            handleContentChange(index, 'digitToChar', e.target.checked)
                                                         }
                                                         size="sm"
                                                         isDisabled={!fieldToggles[contentItem.key]}
                                                         colorScheme="green">
                                                         <Text color={colors.labelText}>Digit to Char</Text>
                                                      </Checkbox>
                                                      <HStack spacing={2}>
                                                         <FormControl>
                                                            <FormLabel fontSize="xs" color={colors.labelText}>
                                                               Operator
                                                            </FormLabel>
                                                            <Select
                                                               size="sm"
                                                               value={contentItem.operator}
                                                               onChange={(e) =>
                                                                  handleContentChange(index, 'operator', e.target.value)
                                                               }
                                                               isDisabled={!fieldToggles[contentItem.key]}
                                                               bg={colors.selectBg}
                                                               borderColor={colors.selectBorder}
                                                               color={colors.selectText}>
                                                               <option value="NONE">None</option>
                                                               <option value="ADD">Add</option>
                                                               <option value="SUBTRACT">Subtract</option>
                                                               <option value="MULTIPLY">Multiply</option>
                                                               <option value="DIVIDE">Divide</option>
                                                            </Select>
                                                         </FormControl>
                                                         <FormControl>
                                                            <FormLabel fontSize="xs" color={colors.labelText}>
                                                               Value
                                                            </FormLabel>
                                                            <NumberInput
                                                               size="sm"
                                                               value={contentItem.operatorValue}
                                                               onChange={(value) =>
                                                                  handleContentChange(
                                                                     index,
                                                                     'operatorValue',
                                                                     parseFloat(value) || 0,
                                                                  )
                                                               }
                                                               isDisabled={!fieldToggles[contentItem.key]}>
                                                               <NumberInputField
                                                                  bg={colors.inputBg}
                                                                  borderColor={colors.inputBorder}
                                                                  color={colors.inputText}
                                                               />
                                                            </NumberInput>
                                                         </FormControl>
                                                      </HStack>
                                                   </VStack>
                                                )}
                                             </VStack>
                                          </Collapse>
                                       </Box>
                                    );
                                 })}
                              </VStack>
                           </CardBody>
                        </Card>

                        {/* Right Column - Generation and Result */}
                        <VStack spacing={4} align="stretch">
                           {/* Generated Barcode Display */}
                           <Card bg={colors.cardBg} borderColor={colors.cardBorder}>
                              <CardBody>
                                 {generatedBarcode ? (
                                    <VStack spacing={2}>
                                       <Text p={2} fontSize="md" fontWeight={'bold'} color={colors.primaryText}>
                                          Barcode Example
                                       </Text>
                                       <Flex gap={3} align="center" w="100%">
                                          <Box
                                             p={3}
                                             bg={colors.barcodeBg}
                                             borderRadius="md"
                                             border="2px solid"
                                             borderColor={colors.barcodeBorder}
                                             w="100%">
                                             <Text
                                                fontSize="xl"
                                                fontFamily="monospace"
                                                textAlign="center"
                                                wordBreak="break-all"
                                                color={colors.barcodeText}
                                                fontWeight="bold"
                                                lineHeight="1.2">
                                                {generatedBarcode}
                                             </Text>
                                          </Box>
                                          <Button size="md" onClick={copyToClipboard} colorScheme="green">
                                             <FaCopy />
                                          </Button>
                                       </Flex>
                                    </VStack>
                                 ) : (
                                    <Text color={colors.secondaryText} textAlign="center" fontStyle="italic">
                                       No barcode generated yet. Click "Generate Barcode" to create one.
                                    </Text>
                                 )}
                                 <Flex gap={3} mt={4}>
                                    <Button
                                       colorScheme="blue"
                                       size="lg"
                                       onClick={generateBarcode}
                                       w="100%"
                                       leftIcon={<Box>🔧</Box>}>
                                       Generate Barcode
                                    </Button>
                                    <Button
                                       variant="outline"
                                       size="lg"
                                       onClick={resetForm}
                                       w="100%"
                                       leftIcon={<Box>🔄</Box>}
                                       borderColor={colors.buttonOutlineBorder}
                                       color={colors.buttonOutlineText}>
                                       Reset Form
                                    </Button>
                                 </Flex>
                              </CardBody>
                           </Card>

                           <Card bg={colors.cardBg} borderColor={colors.cardBorder}>
                              <CardHeader>
                                 <HStack justify="space-between">
                                    <Heading size="sm" color={colors.primaryText}>
                                       Number To Char Replacement
                                    </Heading>
                                    <Badge colorScheme="blue" fontSize="xs">
                                       Applied when Digit to Char is enabled
                                    </Badge>
                                 </HStack>
                              </CardHeader>
                              <CardBody>
                                 <VStack spacing={2}>
                                    {barcodeSetup.alterations.map((alteration, index) => (
                                       <HStack key={index} w="100%">
                                          <FormControl>
                                             <FormLabel fontSize="xs" color={colors.labelText}>
                                                Original
                                             </FormLabel>
                                             <Input
                                                size="xs"
                                                value={alteration.original}
                                                onChange={(e) =>
                                                   handleAlterationChange(index, 'original', e.target.value)
                                                }
                                                placeholder="0-9"
                                                maxLength={1}
                                                bg={colors.inputBg}
                                                borderColor={colors.inputBorder}
                                                color={colors.inputText}
                                                _placeholder={{ color: colors.inputPlaceholder }}
                                             />
                                          </FormControl>
                                          <FormControl>
                                             <FormLabel fontSize="xs" color={colors.labelText}>
                                                Replace
                                             </FormLabel>
                                             <Input
                                                size="xs"
                                                value={alteration.replace}
                                                onChange={(e) =>
                                                   handleAlterationChange(index, 'replace', e.target.value)
                                                }
                                                placeholder="A-Z"
                                                maxLength={1}
                                                bg={colors.inputBg}
                                                borderColor={colors.inputBorder}
                                                color={colors.inputText}
                                                _placeholder={{ color: colors.inputPlaceholder }}
                                             />
                                          </FormControl>
                                          <Button
                                             size="xs"
                                             colorScheme="red"
                                             onClick={() => removeAlteration(index)}
                                             isDisabled={barcodeSetup.alterations?.length <= 1}
                                             mt={4}>
                                             ×
                                          </Button>
                                       </HStack>
                                    ))}
                                    <Button
                                       onClick={addNewAlteration}
                                       colorScheme="blue"
                                       size="xs"
                                       isDisabled={!canAddAlteration()}
                                       opacity={!canAddAlteration() ? 0.5 : 1}>
                                       + Add {!canAddAlteration() && '(Fill current fields first)'}
                                    </Button>
                                 </VStack>
                              </CardBody>
                           </Card>

                           {/* Modifications */}
                           <Card bg={colors.cardBg} borderColor={colors.cardBorder}>
                              <CardHeader>
                                 <HStack justify="space-between">
                                    <Heading size="sm" color={colors.primaryText}>
                                       Char To Special Char Replacement
                                    </Heading>
                                    <Badge colorScheme="blue" fontSize="xs">
                                       Applied when Char to Special is enabled
                                    </Badge>
                                 </HStack>
                              </CardHeader>
                              <CardBody>
                                 <VStack spacing={2}>
                                    {barcodeSetup.modifications.map((modification, index) => (
                                       <HStack key={index} w="100%">
                                          <FormControl>
                                             <FormLabel fontSize="xs" color={colors.labelText}>
                                                Original
                                             </FormLabel>
                                             <Input
                                                size="xs"
                                                value={modification.original}
                                                onChange={(e) =>
                                                   handleModificationChange(index, 'original', e.target.value)
                                                }
                                                placeholder="A-Z"
                                                maxLength={1}
                                                bg={colors.inputBg}
                                                borderColor={colors.inputBorder}
                                                color={colors.inputText}
                                                _placeholder={{ color: colors.inputPlaceholder }}
                                             />
                                          </FormControl>
                                          <FormControl>
                                             <FormLabel fontSize="xs" color={colors.labelText}>
                                                Replace
                                             </FormLabel>
                                             <Input
                                                size="xs"
                                                value={modification.replace}
                                                onChange={(e) =>
                                                   handleModificationChange(index, 'replace', e.target.value)
                                                }
                                                placeholder="Special char"
                                                maxLength={1}
                                                bg={colors.inputBg}
                                                borderColor={colors.inputBorder}
                                                color={colors.inputText}
                                                _placeholder={{ color: colors.inputPlaceholder }}
                                             />
                                          </FormControl>
                                          <Button
                                             size="xs"
                                             colorScheme="red"
                                             onClick={() => removeModification(index)}
                                             isDisabled={barcodeSetup?.modifications?.length <= 1}
                                             mt={4}>
                                             ×
                                          </Button>
                                       </HStack>
                                    ))}
                                    <Button
                                       onClick={addNewModification}
                                       colorScheme="blue"
                                       size="xs"
                                       isDisabled={!canAddModification()}
                                       opacity={!canAddModification() ? 0.5 : 1}>
                                       + Add {!canAddModification() && '(Fill current fields first)'}
                                    </Button>
                                 </VStack>
                              </CardBody>
                           </Card>
                        </VStack>
                     </SimpleGrid>
                  </VStack>
               </Box>
            </ModalBody>

            <ModalFooter bg={colors.modalBg}>
               <HStack spacing={3}>
                  <Button colorScheme="green" onClick={handleBarcodeSave} isDisabled={!barcodeSetup.isEnable}>
                     Save Changes
                  </Button>
                  <Button
                     variant="ghost"
                     onClick={onClose}
                     color={colors.buttonOutlineText}
                     _hover={{ bg: colors.buttonGhostHover }}>
                     Close
                  </Button>
               </HStack>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
}
